import Layout from '../../components/Layout';
import ProductComparison from '../../components/ProductComparison';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';

// Foundation Comparison
const foundationProducts = [
  {
    name: "Skin Tint",
    bestFor: "Natural Look",
    affiliateLink: "#",
    features: {
      coverage: "Sheer",
      finish: "Skin-like",
      longevity: "4-6 hours",
      buildable: true,
      skincare: true,
      shade: "Limited range",
      application: "Fingers or brush",
      skintype: "All types"
    }
  },
  {
    name: "BB Cream",
    bestFor: "Everyday Wear",
    affiliateLink: "#",
    features: {
      coverage: "Light-Medium",
      finish: "Dewy/Natural",
      longevity: "5-7 hours",
      buildable: true,
      skincare: true,
      shade: "Limited range",
      application: "Fingers",
      skintype: "Dry to normal"
    }
  },
  {
    name: "Full Coverage Foundation",
    bestFor: "Special Occasions",
    affiliateLink: "#",
    features: {
      coverage: "Full",
      finish: "Matte or Satin",
      longevity: "8-12 hours",
      buildable: false,
      skincare: false,
      shade: "Wide range",
      application: "Brush or sponge",
      skintype: "Oily to combo"
    }
  }
];

const foundationFeatures = [
  { key: "coverage", name: "Coverage Level" },
  { key: "finish", name: "Finish" },
  { key: "longevity", name: "Wear Time" },
  { key: "buildable", name: "Buildable" },
  { key: "skincare", name: "Skincare Benefits" },
  { key: "shade", name: "Shade Range" },
  { key: "application", name: "Best Application" },
  { key: "skintype", name: "Best For Skin Type" }
];

// Mascara Comparison
const mascaraProducts = [
  {
    name: "Lengthening Mascara",
    bestFor: "Short Lashes",
    affiliateLink: "#",
    features: {
      effect: "Length",
      formula: "Thin, buildable",
      clumping: "Minimal",
      removal: "Easy",
      waterproof: false,
      sensitive: true,
      wand: "Thin, precise",
      coats: "1-2 coats ideal"
    }
  },
  {
    name: "Volumizing Mascara",
    bestFor: "Sparse Lashes",
    affiliateLink: "#",
    features: {
      effect: "Volume",
      formula: "Thick, dramatic",
      clumping: "Possible",
      removal: "Moderate",
      waterproof: false,
      sensitive: true,
      wand: "Thick, fluffy",
      coats: "2-3 coats"
    }
  },
  {
    name: "Waterproof Mascara",
    bestFor: "Long-wear needs",
    affiliateLink: "#",
    features: {
      effect: "Hold + Definition",
      formula: "Firm, lasting",
      clumping: "Minimal",
      removal: "Requires oil cleanser",
      waterproof: true,
      sensitive: false,
      wand: "Varies",
      coats: "1-2 coats"
    }
  }
];

const mascaraFeatures = [
  { key: "effect", name: "Primary Effect" },
  { key: "formula", name: "Formula Type" },
  { key: "clumping", name: "Clumping Risk" },
  { key: "removal", name: "Removal Ease" },
  { key: "waterproof", name: "Waterproof" },
  { key: "sensitive", name: "Sensitive Eye Safe" },
  { key: "wand", name: "Wand Type" },
  { key: "coats", name: "Recommended Coats" }
];

// Blush Comparison
const blushProducts = [
  {
    name: "Cream Blush",
    bestFor: "Dry Skin",
    affiliateLink: "#",
    features: {
      formula: "Cream",
      finish: "Dewy, natural",
      longevity: "4-6 hours",
      blendability: "Excellent",
      application: "Fingers or sponge",
      buildable: true,
      skintype: "Dry to normal",
      skill: "Beginner"
    }
  },
  {
    name: "Powder Blush",
    bestFor: "Oily Skin",
    affiliateLink: "#",
    features: {
      formula: "Powder",
      finish: "Matte or shimmer",
      longevity: "6-8 hours",
      blendability: "Good",
      application: "Brush",
      buildable: true,
      skintype: "All types",
      skill: "Intermediate"
    }
  },
  {
    name: "Liquid Blush",
    bestFor: "Dewy Looks",
    affiliateLink: "#",
    features: {
      formula: "Liquid",
      finish: "Glossy, lit-from-within",
      longevity: "5-7 hours",
      blendability: "Must work fast",
      application: "Fingers or sponge",
      buildable: true,
      skintype: "Dry to combo",
      skill: "Advanced"
    }
  }
];

const blushFeatures = [
  { key: "formula", name: "Formula Type" },
  { key: "finish", name: "Finish" },
  { key: "longevity", name: "Wear Time" },
  { key: "blendability", name: "Blendability" },
  { key: "application", name: "Application Method" },
  { key: "buildable", name: "Buildable" },
  { key: "skintype", name: "Best For Skin Type" },
  { key: "skill", name: "Skill Level" }
];

const MakeupCompare = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="makeup-compare-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/makeup" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Makeup
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Product Comparison
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Compare Makeup Products
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Find your perfect match with our side-by-side product comparisons. From foundations to mascaras, we break down what matters.
          </p>
        </div>
      </section>

      {/* Foundation Comparison */}
      <section data-testid="foundation-comparison" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Base Products
            </h2>
            <p className="text-[#374151]">
              Choose your coverage level based on occasion and preference.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={foundationProducts}
            features={foundationFeatures}
          />
        </div>
      </section>

      {/* Mascara Comparison */}
      <section data-testid="mascara-comparison" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Mascaras
            </h2>
            <p className="text-[#374151]">
              Different formulas for different lash goals.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={mascaraProducts}
            features={mascaraFeatures}
          />
        </div>
      </section>

      {/* Blush Comparison */}
      <section data-testid="blush-comparison" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Blush Formulas
            </h2>
            <p className="text-[#374151]">
              Pick your formula based on skin type and desired finish.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={blushProducts}
            features={blushFeatures}
          />
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice variant="inline" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Ready to build your kit?
          </h3>
          <p className="text-[#374151] mb-6">
            Explore our makeup guides for curated product recommendations.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/makeup/essentials"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29]"
            >
              Makeup Essentials
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/makeup/minimal"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#3A4D39] text-[#3A4D39] text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#3A4D39] hover:text-white"
            >
              Minimal Makeup
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MakeupCompare;
